import { Box, Stack, Typography } from "@mui/material";
import { CalculatedGeneratorStatusSource } from "generated-graphql/graphql";
import { FieldWarningIndicator } from "routes/Customer/Waste/Manifests/FieldWarningIndicator";
import { prettyPrintMonthYear } from "util/dates";

import {
  CalculatedStatusBySource,
  calculatedSourcesDisagree,
  isUsedSourceLine,
  sourceStatusLines,
} from "./calculatedStatusSources";

/**
 * Tooltip body: every source's own answer, with the one the status comes from marked "shown".
 * `month` is set for a year figure, naming the month its sources come from.
 */
function SourcesDifferTooltip({
  sources,
  axis,
  effectiveSource,
  month,
}: {
  sources: readonly CalculatedStatusBySource[];
  axis: "federal" | "state";
  effectiveSource: CalculatedGeneratorStatusSource | null | undefined;
  month?: string | null;
}) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
        Sources differ
      </Typography>
      {month && (
        <Typography variant="caption" component="div" sx={{ mb: 0.25 }}>
          Highest month: {prettyPrintMonthYear(month)}
        </Typography>
      )}
      {sourceStatusLines(sources, axis).map((line) => {
        const used = isUsedSourceLine(line, effectiveSource);
        return (
          <Typography
            key={line.label}
            variant="caption"
            component="div"
            sx={{ fontWeight: used ? 700 : 400 }}
          >
            {line.label}: {line.value}
            {used && " — used"}
          </Typography>
        );
      })}
    </Box>
  );
}

/**
 * A calculated generator status with a (!) when its sources disagree, since only
 * one source is shown and it can differ from the one a user remembers.
 */
export function CalculatedStatusCell({
  label,
  sources,
  axis,
  effectiveSource,
  month,
}: {
  /** The already-formatted status label for the cell. */
  label: string;
  sources: readonly CalculatedStatusBySource[] | null | undefined;
  axis: "federal" | "state";
  /** Which input decided this status, for the tooltip's mark. */
  effectiveSource?: CalculatedGeneratorStatusSource | null;
  /** The month the sources are from, when the cell itself spans a year. */
  month?: string | null;
}) {
  const entries = sources ?? [];
  return (
    <Stack direction="row" alignItems="center" gap={0.25} sx={{ minWidth: 0, height: "100%" }}>
      <Typography variant="body2" noWrap>
        {label}
      </Typography>
      {calculatedSourcesDisagree(entries, axis) && (
        <FieldWarningIndicator
          tooltip={
            <SourcesDifferTooltip
              sources={entries}
              axis={axis}
              effectiveSource={effectiveSource}
              month={month}
            />
          }
          ariaLabel="Sources differ on this calculated status"
          inline
        />
      )}
    </Stack>
  );
}
